import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Dot } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FREQ = ["daily", "weekly", "monthly"];

function kpiStatus(k) {
  if (!k.target) return (k.actual || 0) === 0 ? "green" : "red";
  const diff = Math.abs((k.actual || 0) - k.target) / k.target;
  return diff <= 0.1 ? "green" : diff <= 0.25 ? "amber" : "red";
}

export default function Departments() {
  const { departments } = useApp();
  const { effectiveRole } = useAuth();
  const canWrite = effectiveRole !== "viewer";
  const [kpis, setKpis] = useState([]);
  const [dept, setDept] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/kpis");
    setKpis(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = dept === "all" ? kpis : kpis.filter((k) => k.department_id === dept);
  const grouped = {};
  filtered.forEach((k) => { (grouped[k.department_name || "Other"] ||= []).push(k); });

  const start = (k) => {
    setEditing(k);
    setForm(k || { name: "", department_id: departments[0]?.id, department_name: departments[0]?.name, target: 0, actual: 0, unit: "count", frequency: "monthly" });
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const d = departments.find((x) => x.id === form.department_id);
      const payload = { ...form, department_name: d?.name, target: Number(form.target), actual: Number(form.actual) };
      if (editing?.id) await api.put(`/kpis/${editing.id}`, payload);
      else await api.post("/kpis", payload);
      toast.success("KPI saved");
      setOpen(false); setTimeout(load, 250);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (k) => {
    try { await api.delete(`/kpis/${k.id}`); toast.success("KPI deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="departments-page">
      <PageHeader eyebrow="Operations" title="Department Utilization & KPIs"
        subtitle="Track each department's KPIs against targets with traffic-light status."
        action={
          <div className="flex gap-2">
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="w-[180px]" data-testid="dept-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => start(null)} data-testid="add-kpi-button" disabled={!canWrite} style={{ display: canWrite ? undefined : "none" }}><Plus className="h-4 w-4 mr-1" /> Add KPI</Button>
          </div>
        } />

      <div className="space-y-6">
        {Object.entries(grouped).map(([name, items]) => {
          const util = Math.round(items.reduce((a, k) => a + Math.min(100, (k.actual / (k.target || 1)) * 100), 0) / items.length);
          return (
            <Card key={name} className="p-5" data-testid={`dept-card-${name.split(" ")[0]}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold">{name}</h3>
                <Badge variant="secondary">Utilization {util}%</Badge>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((k) => {
                  const pct = Math.min(100, Math.round((k.actual / (k.target || 1)) * 100));
                  return (
                    <div key={k.id} className="rounded-xl border border-border p-4" data-testid={`kpi-${k.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Dot status={kpiStatus(k)} />
                          <p className="font-medium text-sm">{k.name}</p>
                        </div>
                        <div className="flex">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => start(k)} data-testid={`edit-kpi-${k.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => remove(k)} data-testid={`delete-kpi-${k.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>
                      <p className="mt-2 text-xl font-bold tabular-nums">{k.actual} <span className="text-sm text-muted-foreground font-normal">/ {k.target} {k.unit}</span></p>
                      <Progress value={pct} className="mt-2 h-1.5" />
                      <p className="text-[11px] text-muted-foreground mt-1 capitalize">{k.frequency}</p>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="kpi-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit KPI" : "Add KPI"}</DialogTitle>
            <DialogDescription>Define a KPI with its target, actual and measurement frequency.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="kpi-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Department</Label>
              <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                <SelectTrigger data-testid="kpi-dept-select"><SelectValue /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Target</Label><Input type="number" value={form.target ?? 0} onChange={(e) => setForm({ ...form, target: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Actual</Label><Input type="number" value={form.actual ?? 0} onChange={(e) => setForm({ ...form, actual: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Unit</Label><Input value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="save-kpi-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
